import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, ChevronDown, FileText, Users, Home, PiggyBank, Shield, Building2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useState, useEffect } from "react";
import { useGoogleAnalytics } from "@/hooks/useGoogleAnalytics";

// Optimized WebP logo via vite-imagetools
import navLogo from "@/assets/easy-taxes-nav-logo.png?w=240&format=webp";

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { trackCTA } = useGoogleAnalytics();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {/* Skip to content link for accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:bg-green-primary focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:outline-none"
      >
        Aller au contenu principal
      </a>
      <nav aria-label="Navigation principale" className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b border-green-pastel transition-all duration-300 ${isScrolled ? "bg-white/95 shadow-sm" : "bg-white/80"}`}>
        <div className="container-custom px-4">
          <div className={`flex items-center justify-between transition-all duration-300 ${isScrolled ? "h-12 md:h-14" : "h-14 md:h-16 lg:h-18"}`}>
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="min-h-[44px] flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-green-primary focus-visible:ring-offset-2 rounded-lg" aria-label="Accueil Easy Taxes">
                <img 
                  src={navLogo} 
                  alt="Easy Taxes par Elyx Finance" 
                  width={120}
                  height={40}
                  loading="eager"
                  fetchPriority="high"
                  className={`w-auto transition-all duration-300 ${isScrolled ? "h-7 md:h-8" : "h-8 md:h-9 lg:h-10"}`}
                />
              </Link>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1 lg:gap-2">
              {/* Ma situation fiscale dropdown */}
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="group text-green-primary hover:text-green-secondary hover:bg-green-pastel/40 text-xs lg:text-sm font-medium transition-all duration-300 px-2 lg:px-3 py-1.5 h-9 min-h-[44px] gap-1 data-[state=open]:bg-green-pastel/40"
                  >
                    Ma situation fiscale
                    <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-data-[state=open]:rotate-180" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="start" 
                  className="w-80 bg-white/95 backdrop-blur-xl z-50 p-3 rounded-xl border border-green-pastel/50 shadow-[0_10px_40px_-10px_rgba(11,59,50,0.15),0_4px_20px_-5px_rgba(11,59,50,0.1)] animate-scale-in"
                >
                  <DropdownMenuLabel className="text-[10px] text-muted-foreground font-medium uppercase tracking-widest px-2 py-1.5">
                    Accompagnement fiscal personnalisé
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-green-pastel/40 my-2" />
                  
                  <DropdownMenuItem asChild className="p-0 focus:bg-transparent">
                    <Link 
                      to="/declaration-impots-resident" 
                      className="flex items-center gap-3 px-2 py-3 rounded-lg transition-all duration-300 hover:bg-green-pastel/30 hover:translate-x-1 group/item"
                    >
                      <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-green-pastel/50 transition-all duration-300 group-hover/item:bg-green-pastel group-hover/item:shadow-soft">
                        <Home className="h-4 w-4 text-green-primary" aria-hidden="true" />
                      </div>
                      <div>
                        <div className="font-medium text-green-primary text-sm transition-colors group-hover/item:text-green-secondary">Résident Suisse / Permis C</div>
                        <div className="text-xs text-muted-foreground">Déclaration d'impôts standard</div>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  
                  <DropdownMenuItem asChild className="p-0 focus:bg-transparent">
                    <Link 
                      to="/declaration-impots-permis-b" 
                      className="flex items-center gap-3 px-2 py-3 rounded-lg transition-all duration-300 hover:bg-green-pastel/30 hover:translate-x-1 group/item"
                    >
                      <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-green-pastel/50 transition-all duration-300 group-hover/item:bg-green-pastel group-hover/item:shadow-soft">
                        <FileText className="h-4 w-4 text-green-primary" aria-hidden="true" />
                      </div>
                      <div>
                        <div className="font-medium text-green-primary text-sm transition-colors group-hover/item:text-green-secondary">Résident - Permis B</div>
                        <div className="text-xs text-muted-foreground">Imposition à la source & options</div>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  
                  <DropdownMenuItem asChild className="p-0 focus:bg-transparent">
                    <Link 
                      to="/declaration-impots-frontalier" 
                      className="flex items-center gap-3 px-2 py-3 rounded-lg transition-all duration-300 hover:bg-green-pastel/30 hover:translate-x-1 group/item"
                    >
                      <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-green-pastel/50 transition-all duration-300 group-hover/item:bg-green-pastel group-hover/item:shadow-soft">
                        <Users className="h-4 w-4 text-green-primary" aria-hidden="true" />
                      </div>
                      <div>
                        <div className="font-medium text-green-primary text-sm transition-colors group-hover/item:text-green-secondary">Frontaliers (Permis G)</div>
                        <div className="text-xs text-muted-foreground">DRIS & TOU</div>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mes déductions dropdown */}
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="group text-green-primary hover:text-green-secondary hover:bg-green-pastel/40 text-xs lg:text-sm font-medium transition-all duration-300 px-2 lg:px-3 py-1.5 h-9 min-h-[44px] gap-1 data-[state=open]:bg-green-pastel/40"
                  >
                    Mes déductions
                    <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-data-[state=open]:rotate-180" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="start" 
                  className="w-72 bg-white/95 backdrop-blur-xl z-50 p-3 rounded-xl border border-green-pastel/50 shadow-[0_10px_40px_-10px_rgba(11,59,50,0.15),0_4px_20px_-5px_rgba(11,59,50,0.1)] animate-scale-in"
                >
                  <DropdownMenuLabel className="text-[10px] text-muted-foreground font-medium uppercase tracking-widest px-2 py-1.5">
                    Optimisez votre déclaration
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-green-pastel/40 my-2" />
                  
                  <DropdownMenuItem asChild className="p-0 focus:bg-transparent">
                    <Link 
                      to="/3eme-pilier" 
                      className="flex items-center gap-3 px-2 py-3 rounded-lg transition-all duration-300 hover:bg-green-pastel/30 hover:translate-x-1 group/item"
                    >
                      <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-green-pastel/50 transition-all duration-300 group-hover/item:bg-green-pastel group-hover/item:shadow-soft">
                        <PiggyBank className="h-4 w-4 text-green-primary" aria-hidden="true" />
                      </div>
                      <div>
                        <div className="font-medium text-green-primary text-sm transition-colors group-hover/item:text-green-secondary">3e pilier</div>
                        <div className="text-xs text-muted-foreground">Déduction fiscale & prévoyance</div>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  
                  <DropdownMenuItem asChild className="p-0 focus:bg-transparent">
                    <Link 
                      to="/assurance-maladie-impots" 
                      className="flex items-center gap-3 px-2 py-3 rounded-lg transition-all duration-300 hover:bg-green-pastel/30 hover:translate-x-1 group/item"
                    >
                      <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-green-pastel/50 transition-all duration-300 group-hover/item:bg-green-pastel group-hover/item:shadow-soft">
                        <Shield className="h-4 w-4 text-green-primary" aria-hidden="true" />
                      </div>
                      <div>
                        <div className="font-medium text-green-primary text-sm transition-colors group-hover/item:text-green-secondary">Assurance maladie</div>
                        <div className="text-xs text-muted-foreground">Primes, subsides & frais médicaux</div>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  
                  <DropdownMenuItem asChild className="p-0 focus:bg-transparent">
                    <Link 
                      to="/hypotheque-impots" 
                      className="flex items-center gap-3 px-2 py-3 rounded-lg transition-all duration-300 hover:bg-green-pastel/30 hover:translate-x-1 group/item"
                    >
                      <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-green-pastel/50 transition-all duration-300 group-hover/item:bg-green-pastel group-hover/item:shadow-soft">
                        <Building2 className="h-4 w-4 text-green-primary" aria-hidden="true" />
                      </div>
                      <div>
                        <div className="font-medium text-green-primary text-sm transition-colors group-hover/item:text-green-secondary">Hypothèque & impôts</div>
                        <div className="text-xs text-muted-foreground">Valeur locative & déductions</div>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Parrainage - lien direct niveau 1 */}
              <Link 
                to="/parrainage" 
                className="text-green-primary hover:text-green-secondary text-xs lg:text-sm font-medium transition-all duration-300 px-2 lg:px-3 py-1.5 min-h-[44px] flex items-center hover:bg-green-pastel/30 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-green-primary focus-visible:ring-offset-2"
              >
                Parrainage
              </Link>

              {/* Blog - lien direct niveau 1 */}
              <Link 
                to="/blog" 
                className="text-green-primary hover:text-green-secondary text-xs lg:text-sm font-medium transition-all duration-300 px-2 lg:px-3 py-1.5 min-h-[44px] flex items-center hover:bg-green-pastel/30 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-green-primary focus-visible:ring-offset-2"
              >
                Blog
              </Link>

              {/* Separator */}
              <div className="w-px h-6 bg-green-pastel mx-1 lg:mx-2" role="separator" aria-orientation="vertical" />

              {/* Auth buttons */}
              <a href="https://taxes.elyx-finance.ch/auth" target="_blank" rel="noopener noreferrer" aria-label="Se connecter (ouvre dans un nouvel onglet)" onClick={() => trackCTA('cta_click_login', 'nav_login', 'nav_desktop')}>
                <Button 
                  variant="ghost" 
                  className="bg-green-pastel/40 text-muted-foreground hover:text-green-primary hover:bg-green-pastel text-xs font-medium transition-all duration-300 hover:scale-105 px-3 py-1.5 h-9 min-h-[44px]"
                >
                  Se connecter
                </Button>
              </a>
              
              <a href="https://taxes.elyx-finance.ch/auth?tab=signup" target="_blank" rel="noopener noreferrer" aria-label="Créer un compte (ouvre dans un nouvel onglet)" onClick={() => trackCTA('cta_click_signup', 'nav_signup', 'nav_desktop')}>
                <Button 
                  className="bg-green-primary hover:bg-green-secondary text-white text-xs font-medium rounded-xl shadow-soft hover:shadow-medium transition-all duration-300 hover:scale-105 hover:-translate-y-0.5 px-3 py-1.5 h-9 min-h-[44px]"
                >
                  Créer un compte
                </Button>
              </a>
            </div>

            {/* Mobile hamburger menu */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" className="h-10 w-10 min-h-[44px] min-w-[44px] focus-visible:ring-2 focus-visible:ring-green-primary" aria-label="Ouvrir le menu de navigation">
                  <Menu className="h-5 w-5 text-green-primary" aria-hidden="true" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] bg-white/95 backdrop-blur-xl border-l border-green-pastel/50 shadow-[-10px_0_40px_-10px_rgba(11,59,50,0.15)] overflow-y-auto max-h-screen" role="navigation" aria-label="Menu mobile">
                <SheetTitle className="sr-only">Menu de navigation</SheetTitle>
                <div className="flex flex-col gap-0.5 mt-6 px-1 pb-8">
                  {/* Section: Ma situation fiscale */}
                  <div className="px-2 py-2 mb-1" role="group" aria-labelledby="mobile-nav-situation">
                    <p id="mobile-nav-situation" className="text-[10px] font-medium text-green-primary uppercase tracking-widest mb-0.5">
                      Ma situation fiscale
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Accompagnement fiscal personnalisé
                    </p>
                  </div>
                  
                  <Link 
                    to="/declaration-impots-resident" 
                    className="group flex items-center gap-2.5 rounded-xl px-2 py-2.5 transition-all duration-300 min-h-[48px] [-webkit-tap-highlight-color:transparent] active:bg-green-pastel/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-primary focus-visible:ring-offset-2"
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-green-pastel/50 transition-all duration-300 flex-shrink-0 group-active:bg-green-pastel" aria-hidden="true">
                      <Home className="h-4 w-4 text-green-primary" aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <span className="font-medium text-sm block text-green-primary">Résident Suisse / Permis C</span>
                      <p className="text-xs text-muted-foreground">Déclaration d'impôts standard</p>
                    </div>
                  </Link>
                  
                  <Link 
                    to="/declaration-impots-permis-b" 
                    className="group flex items-center gap-2.5 rounded-xl px-2 py-2.5 transition-all duration-300 min-h-[48px] [-webkit-tap-highlight-color:transparent] active:bg-green-pastel/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-primary focus-visible:ring-offset-2"
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-green-pastel/50 transition-all duration-300 flex-shrink-0 group-active:bg-green-pastel" aria-hidden="true">
                      <FileText className="h-4 w-4 text-green-primary" aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <span className="font-medium text-sm block text-green-primary">Résident - Permis B</span>
                      <p className="text-xs text-muted-foreground">Imposition à la source & options</p>
                    </div>
                  </Link>
                  
                  <Link 
                    to="/declaration-impots-frontalier" 
                    className="group flex items-center gap-2.5 rounded-xl px-2 py-2.5 transition-all duration-300 min-h-[48px] [-webkit-tap-highlight-color:transparent] active:bg-green-pastel/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-primary focus-visible:ring-offset-2"
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-green-pastel/50 transition-all duration-300 flex-shrink-0 group-active:bg-green-pastel" aria-hidden="true">
                      <Users className="h-4 w-4 text-green-primary" aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <span className="font-medium text-sm block text-green-primary">Frontaliers (Permis G)</span>
                      <p className="text-xs text-muted-foreground">DRIS & TOU</p>
                    </div>
                  </Link>
                  
                  <div className="h-px bg-gradient-to-r from-transparent via-green-pastel to-transparent my-2" role="separator" aria-hidden="true" />
                  
                  {/* Section: Mes déductions */}
                  <div className="px-2 py-2 mb-1" role="group" aria-labelledby="mobile-nav-deductions">
                    <p id="mobile-nav-deductions" className="text-[10px] font-medium text-green-primary uppercase tracking-widest mb-0.5">
                      Mes déductions
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Optimisez votre déclaration
                    </p>
                  </div>
                  
                  <Link 
                    to="/3eme-pilier" 
                    className="group flex items-center gap-2.5 rounded-xl px-2 py-2.5 transition-all duration-300 min-h-[48px] [-webkit-tap-highlight-color:transparent] active:bg-green-pastel/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-primary focus-visible:ring-offset-2"
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-green-pastel/50 transition-all duration-300 flex-shrink-0 group-active:bg-green-pastel" aria-hidden="true">
                      <PiggyBank className="h-4 w-4 text-green-primary" aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <span className="font-medium text-sm block text-green-primary">3e pilier</span>
                      <p className="text-xs text-muted-foreground">Déduction fiscale & prévoyance</p>
                    </div>
                  </Link>
                  
                  <Link 
                    to="/assurance-maladie-impots" 
                    className="group flex items-center gap-2.5 rounded-xl px-2 py-2.5 transition-all duration-300 min-h-[48px] [-webkit-tap-highlight-color:transparent] active:bg-green-pastel/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-primary focus-visible:ring-offset-2"
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-green-pastel/50 transition-all duration-300 flex-shrink-0 group-active:bg-green-pastel" aria-hidden="true">
                      <Shield className="h-4 w-4 text-green-primary" aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <span className="font-medium text-sm block text-green-primary">Assurance maladie</span>
                      <p className="text-xs text-muted-foreground">Primes, subsides & frais médicaux</p>
                    </div>
                  </Link>
                  
                  <Link 
                    to="/hypotheque-impots" 
                    className="group flex items-center gap-2.5 rounded-xl px-2 py-2.5 transition-all duration-300 min-h-[48px] [-webkit-tap-highlight-color:transparent] active:bg-green-pastel/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-primary focus-visible:ring-offset-2"
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-green-pastel/50 transition-all duration-300 flex-shrink-0 group-active:bg-green-pastel" aria-hidden="true">
                      <Building2 className="h-4 w-4 text-green-primary" aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <span className="font-medium text-sm block text-green-primary">Hypothèque & impôts</span>
                      <p className="text-xs text-muted-foreground">Valeur locative & déductions</p>
                    </div>
                  </Link>
                  
                  <div className="h-px bg-gradient-to-r from-transparent via-green-pastel to-transparent my-2" role="separator" aria-hidden="true" />
                  
                  {/* Liens directs */}
                  <Link 
                    to="/parrainage" 
                    className="flex items-center font-medium rounded-xl px-2 py-2 transition-all duration-300 min-h-[44px] [-webkit-tap-highlight-color:transparent] text-green-primary active:bg-green-pastel/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-primary focus-visible:ring-offset-2"
                    onClick={() => setIsOpen(false)}
                  >
                    Parrainage
                  </Link>
                  
                  <Link 
                    to="/blog" 
                    className="flex items-center font-medium rounded-xl px-2 py-2 transition-all duration-300 min-h-[44px] [-webkit-tap-highlight-color:transparent] text-green-primary active:bg-green-pastel/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-primary focus-visible:ring-offset-2"
                    onClick={() => setIsOpen(false)}
                  >
                    Blog
                  </Link>
                  
                  <div className="h-px bg-gradient-to-r from-transparent via-green-pastel to-transparent my-2" role="separator" aria-hidden="true" />
                  
                  {/* Auth buttons */}
                  <div className="flex flex-col gap-2 px-1" role="group" aria-label="Connexion et inscription">
                    <a href="https://taxes.elyx-finance.ch/auth" target="_blank" rel="noopener noreferrer" aria-label="Se connecter (ouvre dans un nouvel onglet)" className="w-full [-webkit-tap-highlight-color:transparent] focus:outline-none focus-visible:ring-2 focus-visible:ring-green-primary focus-visible:ring-offset-2 rounded-xl" onClick={() => { trackCTA('cta_click_login', 'nav_login', 'nav_mobile'); setIsOpen(false); }}>
                      <Button 
                        variant="ghost" 
                        className="w-full justify-center bg-green-pastel/40 text-muted-foreground hover:text-green-primary hover:bg-green-pastel font-medium min-h-[48px] rounded-xl transition-all duration-300"
                      >
                        Se connecter
                      </Button>
                    </a>
                    
                    <a href="https://taxes.elyx-finance.ch/auth?tab=signup" target="_blank" rel="noopener noreferrer" aria-label="Créer un compte (ouvre dans un nouvel onglet)" className="w-full [-webkit-tap-highlight-color:transparent] focus:outline-none focus-visible:ring-2 focus-visible:ring-green-primary focus-visible:ring-offset-2 rounded-xl" onClick={() => { trackCTA('cta_click_signup', 'nav_signup', 'nav_mobile'); setIsOpen(false); }}>
                      <Button 
                        className="w-full bg-green-primary hover:bg-green-secondary text-white font-medium rounded-xl shadow-soft hover:shadow-medium min-h-[48px] transition-all duration-300"
                      >
                        Créer un compte
                      </Button>
                    </a>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </>
  );
};
